import { AUDIENCE_LABELS } from '../core/scoring';
import type { ProfileInput, Report } from '../core/types';
import { SEVERITY_LABEL } from './tone';

/**
 * Report export.
 *
 * Two formats, for two different readers:
 *  - Markdown, for a human — paste into a doc, share with a careers adviser, or
 *    work through as a checklist.
 *  - JSON, for a machine — the report plus the profile that produced it, so a
 *    result can be reproduced or diffed later.
 *
 * Both are built as strings here and handed to `downloadFile`, keeping the
 * formatting logic testable without touching the DOM.
 */

function heading(report: Report): string[] {
  const audience = AUDIENCE_LABELS[report.mode].label;
  return [
    '# LinkedIn profile report',
    '',
    `**Score:** ${report.overall}/100 (grade ${report.grade})`,
    `**Written for:** ${audience}`,
    report.keywords.roleLabel
      ? `**Target role:** ${report.keywords.roleLabel} — ${Math.round(report.keywords.coverage * 100)}% keyword coverage`
      : '**Target role:** not specified',
    report.generatedAt ? `**Generated:** ${report.generatedAt}` : '',
    '',
  ].filter((line) => line !== '');
}

export function toMarkdown(report: Report): string {
  const lines: string[] = [...heading(report), ''];

  lines.push('## Scores by section', '');
  lines.push('| Section | Score | Weight |', '| --- | --- | --- |');
  for (const section of report.sections) {
    const score = section.score === null ? 'not scored' : `${section.score}/100`;
    lines.push(`| ${section.label} | ${score} | ${Math.round(section.weight * 100)}% |`);
  }
  lines.push('');

  if (report.topFixes.length > 0) {
    lines.push('## Start here', '');
    report.topFixes.forEach((finding, index) => {
      lines.push(`${index + 1}. **${finding.title}** — ${finding.fix}`);
    });
    lines.push('');
  }

  lines.push('## Everything we found', '');
  if (report.findings.length === 0) {
    lines.push('Nothing outstanding. Every check passed.', '');
  }
  for (const finding of report.findings) {
    lines.push(`### ${finding.title}`, '');
    lines.push(`- **Severity:** ${SEVERITY_LABEL[finding.severity]}`);
    lines.push(`- **Section:** ${finding.section}`);
    lines.push(`- **Why it matters:** ${finding.why}`);
    lines.push(`- **How to fix it:** ${finding.fix}`);
    if (finding.evidence?.length) {
      lines.push(
        `- **Found in your profile:** ${finding.evidence.map((e) => `\`${e}\``).join(', ')}`
      );
    }
    lines.push('');
  }

  if (report.keywords.missing.length > 0) {
    lines.push('## Keywords worth adding', '');
    lines.push(report.keywords.missing.map((keyword) => `\`${keyword}\``).join(', '), '');
  }

  lines.push('## Suggested rewrites', '');
  lines.push(
    '> These are scaffolds, not finished copy. Anything in square brackets is a gap',
    '> only you can fill — replace it with a real number or fact. Never ship a',
    '> placeholder.',
    ''
  );
  for (const [field, variants] of [
    ['Headline', report.rewrites.headline],
    ['About', report.rewrites.about],
  ] as const) {
    lines.push(`### ${field}`, '');
    for (const variant of variants) {
      lines.push(
        `**${variant.label}** — ${variant.description}`,
        '',
        '```',
        variant.text,
        '```',
        ''
      );
    }
  }

  lines.push('---', '', 'Generated by LinkedIn Optimiser — https://github.com');
  lines.push('Not affiliated with or endorsed by LinkedIn Corporation.', '');

  return lines.join('\n');
}

export function toJson(report: Report, profile: ProfileInput): string {
  return JSON.stringify(
    {
      version: 1,
      generatedAt: report.generatedAt,
      mode: report.mode,
      overall: report.overall,
      grade: report.grade,
      sections: report.sections,
      findings: report.findings,
      keywords: report.keywords,
      rewrites: report.rewrites,
      // Included so a report can be reproduced exactly — re-import this and you
      // get the same score back.
      profile,
    },
    null,
    2
  );
}

/** File-safe slug for a download name, e.g. `linkedin-report-72-b.md`. */
export function exportFilename(report: Report, extension: 'md' | 'json'): string {
  return `linkedin-report-${report.overall}-${report.grade.toLowerCase()}.${extension}`;
}

/**
 * Triggers a browser download. The only DOM-touching function in this module,
 * kept separate so the formatters above stay pure and testable.
 */
export function downloadFile(filename: string, contents: string, mime: string): void {
  const blob = new Blob([contents], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoking immediately can race the download in some browsers; a tick is
  // enough and avoids leaking the object URL for the life of the page.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
